/**
 * Data-driven per the spec's own §78 pattern, deliberately trimmed for v1
 * (5 zones, not the full 20) — a plain exported array rather than the
 * content-pipeline machinery (`content/*.json` + a generator script) the
 * achievement catalogue uses. That machinery earns its keep at hundreds of
 * entries; for a handful of zones a typed const is simpler and still
 * genuinely config-driven (nothing here is an if/else chain), and it's a
 * trivial upgrade path later if the zone count grows toward the spec's
 * full 20 — see docs/stack_tower_implementation_plan.md.
 */
export interface StackTowerZonePalette {
  readonly skyTop: string;
  readonly skyBottom: string;
  readonly accent: string;
  readonly haze: string;
}

export interface StackTowerZone {
  readonly id: string;
  readonly label: string;
  /** Blocks climbed (`climbBlocks`), not score — score can now outpace height once the multiplier (spec addendum, rules.ts) kicks in, so zone/altitude tracks the physical tower instead. */
  readonly minHeight: number;
  /** Null means open-ended — satisfies spec §6's "no hard cap" honestly: the palette just continues past the last authored boundary. */
  readonly maxHeight: number | null;
  readonly palette: StackTowerZonePalette;
}

/**
 * Height boundaries are playtesting numbers, not derived from anything load
 * bearing — expect to retune once real players' height distribution is
 * visible (flagged in the implementation plan, not a paper-final answer).
 */
export const STACK_TOWER_ZONES: readonly StackTowerZone[] = [
  {
    id: 'city',
    label: 'STREET LEVEL',
    minHeight: 0,
    maxHeight: 14,
    palette: { skyTop: '#3a2b52', skyBottom: '#d98a4f', accent: '#ff5c35', haze: 'rgba(217,138,79,0.25)' },
  },
  {
    id: 'skyline',
    label: 'SKYLINE',
    minHeight: 15,
    maxHeight: 39,
    palette: { skyTop: '#4a6fa5', skyBottom: '#e8c27a', accent: '#ffb020', haze: 'rgba(232,194,122,0.2)' },
  },
  {
    id: 'clouds',
    label: 'ABOVE THE CLOUDS',
    minHeight: 40,
    maxHeight: 79,
    palette: { skyTop: '#2f6fb0', skyBottom: '#bcdcf5', accent: '#ffffff', haze: 'rgba(255,255,255,0.35)' },
  },
  {
    id: 'high_atmosphere',
    label: 'HIGH ATMOSPHERE',
    minHeight: 80,
    maxHeight: 149,
    palette: { skyTop: '#0c1f4d', skyBottom: '#2f5fa8', accent: '#9de8ff', haze: 'rgba(157,232,255,0.12)' },
  },
  {
    id: 'near_space',
    label: 'NEAR SPACE',
    minHeight: 150,
    maxHeight: null,
    palette: { skyTop: '#03040a', skyBottom: '#0c0d1f', accent: '#f3f1e9', haze: 'rgba(243,241,233,0.06)' },
  },
];

/** The zone containing `height` (blocks climbed). Falls back to the last zone if `height` is somehow past every authored boundary (shouldn't happen — the default list's last zone is open-ended). */
export function zoneForHeight(height: number, zones: readonly StackTowerZone[] = STACK_TOWER_ZONES): StackTowerZone {
  for (const zone of zones) {
    if (height >= zone.minHeight && (zone.maxHeight === null || height <= zone.maxHeight)) return zone;
  }
  return zones[zones.length - 1];
}
